import React, { useState, useEffect, useMemo } from 'react';
import { useAuth } from '../context/AuthContext';
import ConfirmDialog from './ConfirmDialog';
import './BudgetManager.css';


const fmt = (v) => new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(v || 0);

const BudgetManager = ({ onClose, transactions = [] }) => {
  const { authFetch } = useAuth();
  const [budgets, setBudgets]   = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading]   = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing]   = useState(null);
  const [toast, setToast]       = useState(null);
  const [confirmDelete, setConfirmDelete] = useState({ show: false, id: null });
  const [form, setForm] = useState({ categoria: '', valor_limite: '', periodo: 'mensal', cor: '#ef4444' });


  const showToast = (msg, type = 'success') => { setToast({ msg, type }); setTimeout(() => setToast(null), 3500); };

  const fetchAll = async () => {
    try {
      setLoading(true);
      const [bRes, cRes] = await Promise.all([
        authFetch('/api/budgets'),
        authFetch('/api/categories')
      ]);

      if (!bRes.ok || !cRes.ok) throw new Error('Falha na API');

      const bData = await bRes.json();
      const cData = await cRes.json();

      setBudgets(Array.isArray(bData) ? bData : []);
      setCategories(Array.isArray(cData) ? cData : []);
    } catch { 
      showToast('Erro ao carregar dados', 'error'); 
      setBudgets([]);
      setCategories([]);
    } finally { setLoading(false); }
  };

  useEffect(() => { fetchAll(); }, []);

  // Calcula gasto real por categoria (do período atual)
  const gastosPorCategoria = useMemo(() => {
    const map = {};
    (transactions || []).forEach(t => {
      const cat = t.category || '';
      map[cat] = (map[cat] || 0) + parseFloat(t.value || 0);
    });
    return map;
  }, [transactions]);

  const openAdd = () => {
    setForm({ categoria: categories[0]?.nome || '', valor_limite: '', periodo: 'mensal', cor: '#ef4444' });
    setEditing(null);
    setShowForm(true);
  };

  const openEdit = (b) => {
    setForm({ categoria: b.categoria, valor_limite: b.valor_limite, periodo: b.periodo, cor: b.cor || '#ef4444' });
    setEditing(b.id);
    setShowForm(true);
  };

  const handleSave = async (e) => {
    e.preventDefault();
    try {
      const body = { ...form, valor_limite: parseFloat(form.valor_limite) };
      if (editing) {
        await authFetch(`/api/budgets/${editing}`, { method: 'PUT', body: JSON.stringify(body) });
        showToast('Orçamento atualizado!');
      } else {
        await authFetch('/api/budgets', { method: 'POST', body: JSON.stringify(body) });
        showToast('Orçamento criado!');
      }
      setShowForm(false);
      fetchAll();
    } catch (err) { 
      const errorMsg = err.response?.data?.message || err.message || 'Erro ao salvar';
      showToast(errorMsg, 'error'); 
    }
  };

  const handleDelete = async () => {
    const { id } = confirmDelete;
    if (!id) return;
    
    setLoading(true);
    try {
      await authFetch(`/api/budgets/${id}`, { method: 'DELETE' });
      showToast('Orçamento removido!');
      setConfirmDelete({ show: false, id: null });
      fetchAll();
    } catch { 
      showToast('Erro ao remover', 'error'); 
    } finally {
      setLoading(false);
    }
  };


  return (
    <div className="bgt-overlay" onClick={(e) => e.target === e.currentTarget && onClose()}>
      <div className="bgt-modal">
        {toast && <div className={`bgt-toast ${toast.type}`}>{toast.type === 'success' ? '✅' : '❌'} {toast.msg}</div>}

        <div className="bgt-header">
          <div>
            <h2>💰 Orçamentos por Categoria</h2>
            <p>{budgets.length} orçamentos configurados</p>
          </div>
          <div className="bgt-header-actions">
            <button className="bgt-btn-primary" onClick={openAdd}>+ Novo</button>
            <button className="modal-close-btn" onClick={onClose}>✕</button>
          </div>
        </div>

        <div className="bgt-list">
          {loading ? (
            <div className="bgt-empty">⏳ Carregando...</div>
          ) : !Array.isArray(budgets) || budgets.length === 0 ? (
            <div className="bgt-empty">
              <span>💰</span>
              <p>Nenhum orçamento configurado</p>
              <button className="bgt-btn-primary" onClick={openAdd}>Configurar primeiro orçamento</button>
            </div>
          ) : (
            budgets.map(b => {
              const gasto     = gastosPorCategoria[b.categoria] || 0;
              const pct       = Math.min(100, (gasto / (b.valor_limite || 1)) * 100);
              const restante  = b.valor_limite - gasto;
              const isOver    = gasto > b.valor_limite;
              const isWarning = pct >= 80 && !isOver;
              return (
                <div key={b.id} className={`bgt-item ${isOver ? 'over' : isWarning ? 'warning' : ''}`}>
                  <div className="bgt-item-top">
                    <div className="bgt-dot" style={{ background: b.cor }} />
                    <div className="bgt-info">
                      <strong>{b.categoria}</strong>
                      <span className="bgt-periodo">{b.periodo}</span>
                    </div>
                    <div className="bgt-values">
                      <span className="bgt-spent" style={{ color: isOver ? '#ef4444' : isWarning ? '#f59e0b' : 'inherit' }}>{fmt(gasto)}</span>
                      <span className="bgt-limit">/ {fmt(b.valor_limite)}</span>
                    </div>
                    <div className="bgt-actions">
                      <button onClick={() => openEdit(b)} className="bgt-btn-sm">✏️</button>
                      <button onClick={() => setConfirmDelete({ show: true, id: b.id })} className="bgt-btn-sm">🗑️</button>
                    </div>

                  </div>
                  <div className="bgt-bar">
                    <div className="bgt-bar-fill" style={{ width: `${pct}%`, background: isOver ? '#ef4444' : isWarning ? '#f59e0b' : b.cor }} />
                  </div>
                  <div className="bgt-bar-label">
                    <span>{pct.toFixed(1)}% utilizado</span>
                    <span className={restante < 0 ? 'over-label' : ''}>
                      {restante < 0 ? `⚠️ ${fmt(Math.abs(restante))} acima do limite` : `${fmt(restante)} disponível`}
                    </span>
                  </div>
                </div>
              );
            })
          )}
        </div>

        {showForm && (
          <div className="bgt-form-overlay" onClick={(e) => e.target === e.currentTarget && setShowForm(false)}>
            <div className="bgt-form-modal">
              <div className="bgt-form-header">
                <h3>{editing ? 'Editar Orçamento' : 'Novo Orçamento'}</h3>
                <button className="modal-close-btn" onClick={() => setShowForm(false)}>✕</button>
              </div>
              <form onSubmit={handleSave} className="bgt-form">
                <div className="gf-field">
                  <label>Categoria *</label>
                  <select required value={form.categoria} onChange={e => setForm(p => ({ ...p, categoria: e.target.value }))}>
                    <option value="">Selecione...</option>
                    {Array.isArray(categories) && categories.filter(c => (c.tipo || '').toLowerCase() === 'despesa').map(c => (
                      <option key={c.id} value={c.nome}>{c.nome}</option>
                    ))}
                  </select>
                </div>
                <div className="gf-row">
                  <div className="gf-field">
                    <label>Valor Limite (R$) *</label>
                    <input required type="number" step="0.01" min="0" placeholder="0,00"
                      value={form.valor_limite} onChange={e => setForm(p => ({ ...p, valor_limite: e.target.value }))} />
                  </div>
                  <div className="gf-field">
                    <label>Período</label>
                    <select value={form.periodo} onChange={e => setForm(p => ({ ...p, periodo: e.target.value }))}>
                      <option value="mensal">Mensal</option>
                      <option value="semanal">Semanal</option>
                      <option value="anual">Anual</option>
                    </select>
                  </div>
                </div>
                <div className="gf-field">
                  <label>Cor de alerta</label>
                  <input type="color" value={form.cor} onChange={e => setForm(p => ({ ...p, cor: e.target.value }))} />
                </div>
                <div className="gf-actions">
                  <button type="button" className="gf-btn-cancel" onClick={() => setShowForm(false)}>Cancelar</button>
                  <button type="submit" className="gf-btn-save">Salvar</button>
                </div>
              </form>
            </div>
          </div>
        )}

        <ConfirmDialog 
          show={confirmDelete.show}
          title="Excluir Orçamento?"
          message={`Ao excluir este orçamento, você deixará de monitorar o limite para a categoria "${budgets.find(b => b.id === confirmDelete.id)?.categoria}".`}
          confirmText="Sim, excluir"
          cancelText="Não, manter"
          onConfirm={handleDelete}
          onCancel={() => setConfirmDelete({ show: false, id: null })}
          type="danger"
          isLoading={loading}
        />
      </div>
    </div>

  );
};

export default BudgetManager;
